import { logger } from './logger';
import Cookies from 'js-cookie';
import type { User } from '../types/auth';

// Constants
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_DATA_KEY = 'userData';
const TOKEN_DATA_KEY = 'tokenData';
const DB_NAME = 'EzTopupTokenDB';
const DB_VERSION = 1;
const STORE_NAME = 'tokens';

interface TokenData {
  accessToken: string;
  refreshToken: string;
  userData: User;
  timestamp: number;
}

interface StoredTokenData {
  accessToken?: string;
  refreshToken?: string;
  userData?: User;
  accessTokenExpiry?: number;
  refreshTokenExpiry?: number;
}

class TokenStorage {
  private static instance: TokenStorage;
  private dbPromise: Promise<IDBDatabase> | null = null;

  private constructor() {
    this.initIndexedDB();
  }

  public static getInstance(): TokenStorage {
    if (!TokenStorage.instance) {
      TokenStorage.instance = new TokenStorage();
    }
    return TokenStorage.instance;
  }

  private async initIndexedDB(): Promise<void> {
    if (typeof window === 'undefined' || !window.indexedDB) {
      logger.warn('IndexedDB not available');
      return;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
    });
  }

  // Store tokens with enhanced persistence
  public async setTokens(accessToken: string, refreshToken: string, userData: User): Promise<void> {
    const tokenData: TokenData = {
      accessToken,
      refreshToken,
      userData,
      timestamp: Date.now(),
    };

    try {
      // Store in localStorage as plain text
      localStorage.setItem(TOKEN_DATA_KEY, JSON.stringify(tokenData));

      // Also store individual items for backward compatibility
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));

      // Store in IndexedDB as backup
      await this.storeInIndexedDB(tokenData);
    } catch (error) {
      logger.error('Failed to store tokens:', error);
    }
  }

  // Get access token with validation
  public getAccessToken(): string | null {
    try {
      // Try to get from individual storage first (more reliable)
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (token && this.isValidToken(token)) {
        return token;
      }

      // Fallback to combined storage
      const tokenData = this.getTokenData();
      if (tokenData?.accessToken && this.isValidToken(tokenData.accessToken)) {
        return tokenData.accessToken;
      }

      // Fallback to cookie
      const cookieToken = Cookies.get('accessToken');
      if (cookieToken && this.isValidToken(cookieToken)) {
        return cookieToken;
      }

      return null;
    } catch (error) {
      logger.error('Failed to get access token:', error);
      // Try to get from individual storage as last resort
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (token && this.isValidToken(token)) {
        return token;
      }
      // Fallback to cookie as last resort
      const cookieToken = Cookies.get('accessToken');
      return cookieToken && this.isValidToken(cookieToken) ? cookieToken : null;
    }
  }

  // Get refresh token with validation
  public getRefreshToken(): string | null {
    try {
      // Prefer returning whatever is stored; refresh tokens may be opaque strings
      const token = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (token) {
        return token;
      }

      // Fallback to combined storage
      const tokenData = this.getTokenData();
      if (tokenData?.refreshToken) {
        return tokenData.refreshToken;
      }

      // Fallback to cookie
      const cookieToken = Cookies.get('refreshToken');
      if (cookieToken) {
        return cookieToken;
      }

      return null;
    } catch (error) {
      logger.error('Failed to get refresh token:', error);
      // Try to get from individual storage as last resort
      const token = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (token) {
        return token;
      }
      // Fallback to cookie as last resort
      const cookieToken = Cookies.get('refreshToken');
      return cookieToken || null;
    }
  }

  // Get user data
  public getUserData(): User | null {
    try {
      // Try to get from combined storage first
      const tokenData = this.getTokenData();
      if (tokenData?.userData) {
        return tokenData.userData;
      }

      // Fallback to individual storage
      const userData = localStorage.getItem(USER_DATA_KEY);
      if (userData) {
        try {
          return JSON.parse(userData);
        } catch (parseError) {
          logger.error('Failed to parse user data from individual storage:', parseError);
          // Clear corrupted individual user data
          localStorage.removeItem(USER_DATA_KEY);
        }
      }

      // Fallback to cookie
      const userCookie = Cookies.get('user');
      if (userCookie) {
        try {
          return JSON.parse(userCookie) as User;
        } catch (parseError) {
          logger.error('Failed to parse user data from cookie:', parseError);
          Cookies.remove('user');
        }
      }
      return null;
    } catch (error) {
      logger.error('Failed to get user data:', error);
      // If there's a decryption error, clear all corrupted data
      if (error instanceof Error && error.message.includes('Malformed UTF-8')) {
        logger.warn('UTF-8 decryption error detected, clearing all token data');
        this.clearCorruptedData();
      }
      return null;
    }
  }

  // Get combined token data
  private getTokenData(): TokenData | null {
    try {
      const tokenDataStr = localStorage.getItem(TOKEN_DATA_KEY);
      if (!tokenDataStr) return null;

      // Validate token data format
      if (typeof tokenDataStr !== 'string' || tokenDataStr.length < 10) {
        logger.warn('Invalid token data format, clearing corrupted data');
        this.clearCorruptedData();
        return null;
      }

      // Check for obviously corrupted data patterns
      if (
        tokenDataStr.includes('undefined') ||
        tokenDataStr.includes('null') ||
        tokenDataStr.includes('[object Object]')
      ) {
        logger.warn('Corrupted token data detected, clearing all token data');
        this.clearCorruptedData();
        return null;
      }

      const tokenData = JSON.parse(tokenDataStr);

      // Validate token data structure
      if (!tokenData || typeof tokenData !== 'object') {
        throw new Error('Invalid token data structure');
      }

      return tokenData;
    } catch (error) {
      logger.error('Failed to get token data:', error);
      // Clear all corrupted data and try to recover from individual storage
      this.clearCorruptedData();
      return null;
    }
  }

  // Check if tokens exist
  public hasTokens(): boolean {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    return !!(accessToken && refreshToken);
  }

  // Clear all tokens
  public clearTokens(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_DATA_KEY);
      localStorage.removeItem(TOKEN_DATA_KEY);

      // Also clear cookies
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
      Cookies.remove('user');

      // Clear from IndexedDB
      this.clearFromIndexedDB();
    } catch (error) {
      logger.error('Failed to clear tokens:', error);
    }
  }

  // Clear corrupted data
  private clearCorruptedData(): void {
    try {
      logger.warn('Clearing corrupted token data');
      localStorage.removeItem(TOKEN_DATA_KEY);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_DATA_KEY);

      // Also clear cookies
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
      Cookies.remove('user');

      // Clear from IndexedDB
      this.clearFromIndexedDB();
    } catch (error) {
      logger.error('Failed to clear corrupted data:', error);
    }
  }

  // Force reset - clear everything
  public forceReset(): void {
    try {
      // Clear all possible keys
      const keysToRemove = [
        ACCESS_TOKEN_KEY,
        REFRESH_TOKEN_KEY,
        USER_DATA_KEY,
        TOKEN_DATA_KEY,
        'user', // Legacy cookie key
        'accessToken', // Legacy cookie key
        'refreshToken', // Legacy cookie key
      ];

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      // Also clear cookies explicitly
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
      Cookies.remove('user');

      // Clear IndexedDB
      this.clearFromIndexedDB();
    } catch (error) {
      logger.error('Failed to force reset:', error);
    }
  }

  // Check and repair data corruption
  public async checkAndRepairData(): Promise<boolean> {
    try {
      logger.log('Starting data check and repair process...');

      // First, try to get tokens using the standard methods
      const accessToken = this.getAccessToken();
      const refreshToken = this.getRefreshToken();
      const userData = this.getUserData();

      logger.log('Token check results:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        hasUserData: !!userData,
      });

      // If we have all required data and tokens are valid, we're good
      if (accessToken && refreshToken && userData) {
        const accessValid = this.isValidToken(accessToken);
        const refreshValid = this.isValidToken(refreshToken);

        logger.log('Token validation results:', {
          accessTokenValid: accessValid,
          refreshTokenValid: refreshValid,
        });

        if (accessValid && refreshValid) {
          logger.log('All tokens are valid, no recovery needed');
          return true;
        }
      }

      // If we reach here, we need to attempt recovery
      logger.log('Tokens missing or invalid, attempting recovery from backup');
      return await this.recoverFromBackup();
    } catch (error) {
      logger.error('Failed to check and repair data:', error);
      return false;
    }
  }

  // Recover from backup sources
  private async recoverFromBackup(): Promise<boolean> {
    try {
      logger.log('Starting backup recovery process...');

      // Step 1: Try to recover from individual localStorage keys
      logger.log('Step 1: Checking individual localStorage keys');
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      const userDataStr = localStorage.getItem(USER_DATA_KEY);

      logger.log('Individual storage check:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        hasUserData: !!userDataStr,
      });

      if (accessToken && refreshToken && userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          const accessValid = this.isValidToken(accessToken);
          const refreshValid = this.isValidToken(refreshToken);

          logger.log('Individual token validation:', {
            accessTokenValid: accessValid,
            refreshTokenValid: refreshValid,
          });

          if (accessValid && refreshValid) {
            logger.log('✅ Recovery successful from individual localStorage');
            await this.setTokens(accessToken, refreshToken, userData);
            return true;
          } else {
            logger.log('❌ Individual tokens are invalid');
          }
        } catch (parseError) {
          logger.error('Failed to parse user data from individual storage:', parseError);
        }
      }

      // Step 2: Try to recover from combined localStorage key
      logger.log('Step 2: Checking combined localStorage data');
      const combinedData = localStorage.getItem(TOKEN_DATA_KEY);
      if (combinedData) {
        logger.log('Combined data found, attempting to parse...');
        try {
          const tokenData: TokenData = JSON.parse(combinedData);

          const accessValid = this.isValidToken(tokenData.accessToken);
          const refreshValid = this.isValidToken(tokenData.refreshToken);

          logger.log('Combined token validation:', {
            accessTokenValid: accessValid,
            refreshTokenValid: refreshValid,
          });

          if (accessValid && refreshValid) {
            logger.log('✅ Recovery successful from combined localStorage');
            await this.setTokens(tokenData.accessToken, tokenData.refreshToken, tokenData.userData);
            return true;
          } else {
            logger.log('❌ Combined tokens are invalid');
          }
        } catch (error) {
          logger.error('Failed to recover from combined data:', error);
        }
      } else {
        logger.log('No combined data found');
      }

      // Step 3: Try to recover from IndexedDB
      logger.log('Step 3: Checking IndexedDB');
      const dbData = await this.getFromIndexedDB();
      if (dbData) {
        logger.log('IndexedDB data found, validating tokens...');
        const accessValid = this.isValidToken(dbData.accessToken);
        const refreshValid = this.isValidToken(dbData.refreshToken);

        logger.log('IndexedDB token validation:', {
          accessTokenValid: accessValid,
          refreshTokenValid: refreshValid,
        });

        if (accessValid && refreshValid) {
          logger.log('✅ Recovery successful from IndexedDB');
          await this.setTokens(dbData.accessToken, dbData.refreshToken, dbData.userData);
          return true;
        } else {
          logger.log('❌ IndexedDB tokens are invalid');
        }
      } else {
        logger.log('No IndexedDB data found');
      }

      // Step 4: Try to recover from cookies
      logger.log('Step 4: Checking cookies');
      const cookieAccess = Cookies.get('accessToken');
      const cookieRefresh = Cookies.get('refreshToken');
      const userCookieStr = Cookies.get('user');

      logger.log('Cookie storage check:', {
        hasAccessToken: !!cookieAccess,
        hasRefreshToken: !!cookieRefresh,
        hasUserData: !!userCookieStr,
      });

      if (cookieAccess && cookieRefresh && userCookieStr) {
        try {
          const userData = JSON.parse(userCookieStr) as User;
          const accessValid = this.isValidToken(cookieAccess);
          const refreshValid = this.isValidToken(cookieRefresh);

          logger.log('Cookie token validation:', {
            accessTokenValid: accessValid,
            refreshTokenValid: refreshValid,
          });

          if (accessValid && refreshValid) {
            logger.log('✅ Recovery successful from cookies');
            await this.setTokens(cookieAccess, cookieRefresh, userData);
            return true;
          } else {
            logger.log('❌ Cookie tokens are invalid');
          }
        } catch (parseError) {
          logger.error('Failed to parse user data from cookies:', parseError);
        }
      } else {
        logger.log('No valid cookies found for recovery');
      }

      logger.log('❌ All recovery attempts failed');
      return false;
    } catch (error) {
      logger.error('Failed to recover from backup:', error);
      return false;
    }
  }

  // Store in IndexedDB
  private async storeInIndexedDB(tokenData: TokenData): Promise<void> {
    try {
      if (!this.dbPromise) return;

      const db = await this.dbPromise;
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await store.put({ id: 'tokens', data: JSON.stringify(tokenData) });
    } catch {
      logger.error('Failed to store in IndexedDB');
    }
  }

  // Get from IndexedDB
  private async getFromIndexedDB(): Promise<TokenData | null> {
    try {
      if (!this.dbPromise) return null;

      const db = await this.dbPromise;
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      return new Promise((resolve) => {
        const request = store.get('tokens');
        request.onsuccess = () => {
          if (request.result?.data) {
            try {
              resolve(JSON.parse(request.result.data));
            } catch (error) {
              logger.error('Failed to parse IndexedDB data:', error);
              resolve(null);
            }
          } else {
            resolve(null);
          }
        };
        request.onerror = () => resolve(null);
      });
    } catch {
      logger.error('Failed to get from IndexedDB');
      return null;
    }
  }

  // Clear from IndexedDB
  private async clearFromIndexedDB(): Promise<void> {
    try {
      if (!this.dbPromise) return;

      const db = await this.dbPromise;
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await store.delete('tokens');
    } catch {
      logger.error('Failed to clear from IndexedDB');
    }
  }

  // Check if token is about to expire (within specified minutes)
  public isTokenAboutToExpire(token: string, withinMinutes: number = 5): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;

      return timeUntilExpiry <= withinMinutes * 60 * 1000;
    } catch {
      return true; // Consider invalid tokens as expired
    }
  }

  // Get time until token expiry (async version)
  public async getTimeUntilExpiry(token: string): Promise<number> {
    return this.getTimeUntilExpirySync(token);
  }

  // Get time until token expiry (sync version)
  public getTimeUntilExpirySync(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000;
      const currentTime = Date.now();
      return Math.max(0, expiryTime - currentTime);
    } catch {
      return 0; // Return 0 for invalid tokens
    }
  }

  // Validate JWT token format
  public isValidToken(token: string): boolean {
    if (!token || typeof token !== 'string') {
      logger.debug('Invalid token: not a string or empty');
      return false;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      logger.debug('Invalid token: incorrect number of parts:', parts.length);
      return false;
    }

    try {
      // Try to decode the header
      const header = JSON.parse(atob(parts[0]));
      if (!header.alg || !header.typ) {
        logger.debug('Invalid token: missing algorithm or type in header');
        return false;
      }

      // Try to decode the payload
      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp) {
        logger.debug('Invalid token: missing expiration time');
        return false;
      }

      // Check if token is expired
      const currentTime = Date.now() / 1000;
      if (payload.exp < currentTime) {
        logger.debug('Token is expired');
        return false;
      }

      // iat (issued at) is optional for some tokens like refresh tokens
      return true;
    } catch (error) {
      logger.debug('Invalid token: failed to decode', error);
      return false;
    }
  }

  // Check if token is expired
  public isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch {
      return true; // Consider invalid tokens as expired
    }
  }

  // Validate token data structure
  public isValidTokenData(data: unknown): data is StoredTokenData {
    if (!data || typeof data !== 'object' || data === null) {
      return false;
    }

    const obj = data as Record<string, unknown>;
    return (
      (typeof obj.accessToken === 'string' || obj.accessToken === undefined) &&
      (typeof obj.refreshToken === 'string' || obj.refreshToken === undefined)
    );
  }
}

export const tokenStorage = TokenStorage.getInstance();
export default TokenStorage;
